/**
 * Pure reducer for the BATCH composite action — Phase FB.9 of
 * docs/design/frontend-mv-architecture-260524.md §P.5.
 *
 * `BATCH` wraps N sub-ops into a single dispatch. The reducer folds the
 * sub-ops sequentially against the starting IR, producing ONE final IR
 * and a `dirtyPages` Set equal to the UNION of every sub-op's
 * `dirtyPages`. The dispatcher then commits ONCE and notifies subscribers
 * ONCE — no interleaving, no multi-dispatch race.
 *
 * Constraints (enforced by this reducer):
 *   - Nested BATCH is forbidden — `ops` MUST NOT contain a BATCH itself.
 *     Throws IRError("NestedBatch"). Callers flatten before dispatch.
 *   - An empty `ops` array is a valid no-op: returns the input IR + an
 *     empty `dirtyPages`. The dispatcher skips the rebuild + skips
 *     subscriber notifications (consistent with idempotent delete-element).
 *   - Sub-op errors propagate unchanged — a failing sub-op aborts the
 *     entire BATCH. The dispatch contract is "commit-on-success, never
 *     on partial"; a partial commit would leave `state.ir` reflecting
 *     SOME of the BATCH and `dirtyPages` reflecting SOME of the union,
 *     a non-atomic boundary the caller has no way to repair.
 *
 * The `apply` import here is the same `apply` that this file's batchReducer
 * is dispatched from — but called RECURSIVELY through `apply`, not the
 * per-kind reducer functions, so adding a new op-kind to the apply switch
 * automatically makes the new kind valid inside a BATCH (no per-kind
 * wire-up needed).
 *
 * Layer: reducers. Allowed imports: store/, actions/, util/. MUST NOT
 * import views/, controllers/, selectors/.
 *
 * @module reducers/batch-reducer
 */
import { IRError, apply, } from "../ir.js";
export function batchReducer(ir, op) {
    if (!Array.isArray(op.ops)) {
        throw new IRError("InvalidBatch", "BATCH.ops must be an array");
    }
    if (op.ops.length === 0) {
        return { ir, dirtyPages: new Set() };
    }
    let nextIr = ir;
    const dirtyPages = new Set();
    for (let i = 0; i < op.ops.length; i++) {
        const sub = op.ops[i]; // WHY: index < length guarded by the for-loop bound
        if (sub.kind === "BATCH") {
            throw new IRError("NestedBatch", `BATCH cannot contain a nested BATCH at ops[${i}]; flatten before dispatch`);
        }
        const result = apply(nextIr, sub);
        nextIr = result.ir;
        for (const p of result.dirtyPages)
            dirtyPages.add(p);
    }
    return { ir: nextIr, dirtyPages };
}
